import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { useAppContext } from '../../../../../context/AppContext';
import {
  updateFormTemplateTrialContainers as updateFormTemplateTrialContainersAction,
  setFormTemplatesError as setFormTemplatesErrorAction,
} from '../../redux/actions/formTemplateApiActions';
import { fetchTrialContainers as fetchTrialContainersAction } from '../../redux/actions/trialContainerApiActions';
import staticText from '../../staticText.json';
import { handleFormTemplateFailure, handleFormTemplateSuccess } from '../../utils/formTemplateNotifications';
import { formTemplateHasTrialContainer, requiredFormTemplateInUse } from '../../utils/utils';
import FormTemplateFooter from '../presentational/formTemplate/FormTemplateFooter/FormTemplateFooter';
import FormTemplateTrialContainerList from './FormTemplateTrialContainerList';

export function FormTemplateTrialContainersContainer(props) {
  const {
    formTemplate = undefined,
    fetchTrialContainers,
    accountId = undefined,
    allTrialContainers,
    allTrialContainerIds,
    trialContainerRequestError = {},
    trialContainerRequestLoading = false,
    updateFormTemplateTrialContainers,
    setFormTemplatesError,
    formTemplateRequest = {},
  } = props;

  const { notificationDomRef } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [displayedTrialContainers, setDisplayedTrialContainers] = useState([]);
  const [initialDisplayedTrialContainers, setInitialDisplayedTrialContainers] = useState([]);
  const { requestType } = formTemplateRequest;

  useEffect(() => {
    if (accountId !== undefined) {
      fetchTrialContainers();
    }
  }, [accountId, fetchTrialContainers]);

  const orderByActiveAndName = (parsedTrialContainers) =>
    parsedTrialContainers.sort((a, b) => b.active - a.active || a.name?.localeCompare(b?.name));
  // get the trialContainers fetched from the API call and create a new array from them which
  // includes whether they are active in the current form template or not
  // this is what we'll render and update
  useEffect(() => {
    if (formTemplate && allTrialContainers) {
      let allTrialContainersOrdered = allTrialContainerIds.map((trialContainerId) => {
        return {
          id: allTrialContainers[trialContainerId]?.id,
          name: allTrialContainers[trialContainerId]?.name,
          active: formTemplateHasTrialContainer(formTemplate, allTrialContainers[trialContainerId]?.id),
          requiredFormTemplateInUse: requiredFormTemplateInUse(formTemplate, trialContainerId),
        };
      });

      allTrialContainersOrdered = orderByActiveAndName(allTrialContainersOrdered);
      setDisplayedTrialContainers(allTrialContainersOrdered);
      setInitialDisplayedTrialContainers(allTrialContainersOrdered);
    }
  }, [formTemplate, allTrialContainers]);

  useEffect(() => {
    return () => {
      if (requestType) setFormTemplatesError({}, null);
    };
  }, [requestType]);

  // checkbox has been clicked - toggle the true/false state for that trialContainer
  const toggleTrialContainerActiveState = (trialContainerId) => {
    const trialContainerIndex = displayedTrialContainers.findIndex(
      (trialContainer) => trialContainer.id === trialContainerId,
    );
    let newDisplayedTrialContainers = displayedTrialContainers.slice();
    newDisplayedTrialContainers[trialContainerIndex] = {
      ...displayedTrialContainers[trialContainerIndex],
      active: !displayedTrialContainers[trialContainerIndex].active,
    };
    newDisplayedTrialContainers = orderByActiveAndName(newDisplayedTrialContainers);
    setDisplayedTrialContainers(newDisplayedTrialContainers);
  };

  const handleSave = async () => {
    if (isLoading) return;
    // get all selected trialContainers as an array of ids
    const selectedtrialContainerIds = displayedTrialContainers
      .filter((trialContainer) => trialContainer.active)
      .map((trialContainer) => trialContainer.id);

    try {
      setIsLoading(true);
      await updateFormTemplateTrialContainers(formTemplate.id, selectedtrialContainerIds);
      setIsLoading(false);
      handleFormTemplateSuccess(notificationDomRef, setFormTemplatesError, 'saveTrialContainer');
    } catch (error) {
      handleFormTemplateFailure(error, notificationDomRef, setFormTemplatesError, 'saveTrialContainer');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedTrialContainersUnchanged = () => {
    return _.isEqual(displayedTrialContainers, initialDisplayedTrialContainers);
  };

  const saveButtonProps = {
    handleFormAction: handleSave,
    isDisable: isLoading || allTrialContainerIds.length === 0 || selectedTrialContainersUnchanged(),
    showLoadingIcon: isLoading,
    buttonTitle: staticText.buttons.update_form,
  };

  return (
    <>
      <FormTemplateTrialContainerList
        allTrialContainers={displayedTrialContainers}
        trialContainerRequestLoading={trialContainerRequestLoading}
        trialContainerRequestError={trialContainerRequestError}
        onSelectTrialContainer={toggleTrialContainerActiveState}
      />
      <FormTemplateFooter saveButton={saveButtonProps} />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    allTrialContainers: state.trialContainerApiReducer.trialContainers,
    allTrialContainerIds: state.trialContainerApiReducer.trialContainerAllIds,
    accountId: state.internalReducer.internal.accountId,
    trialContainerRequestError: state.trialContainerApiReducer.trialContainerRequest.fetchRequest.error,
    trialContainerRequestLoading: state.trialContainerApiReducer.trialContainerRequest.fetchRequest.loading,
    formTemplateRequest: state.formTemplateApiReducer.formTemplateRequest,
  };
};

const mapDispatchToProps = {
  fetchTrialContainers: fetchTrialContainersAction,
  updateFormTemplateTrialContainers: updateFormTemplateTrialContainersAction,
  setFormTemplatesError: setFormTemplatesErrorAction,
};

FormTemplateTrialContainersContainer.propTypes = {
  formTemplate: PropTypes.shape(),
  fetchTrialContainers: PropTypes.func.isRequired,
  accountId: PropTypes.number,
  allTrialContainers: PropTypes.shape().isRequired,
  allTrialContainerIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  trialContainerRequestError: PropTypes.shape(),
  trialContainerRequestLoading: PropTypes.bool,
  updateFormTemplateTrialContainers: PropTypes.func.isRequired,
  setFormTemplatesError: PropTypes.func.isRequired,
  formTemplateRequest: PropTypes.shape(),
};

export default connect(mapStateToProps, mapDispatchToProps)(FormTemplateTrialContainersContainer);
